import { useEffect, useState } from "react";
import { Plus, Trash2, Copy, ExternalLink, FileText, Download, Edit2, ChevronDown, ChevronUp, Sparkles, Save, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";

const BLANK_DELIV = {
  title: "", synopsis: "", artwork_urls: "", trailer_youtube_id: "",
  pitch_deck_url: "", metadata: "", download_urls: "", passkey: "",
};

const BLANK_META = {
  id: null, title: "", content_type: "Feature Film",
  imdb_id: "", provider_episode_id: "", provider_series_id: "",
  series_title: "", episode_title: "", season_number: "", episode_number: "",
  description: "",
  release_date: "", available_date: "", expiration_date: "",
  parental_rating: "", languages: "", countries: "", genres: "",
  directors: "", writers: "", producers: "", actors: "",
  keywords: "", cue_points: "",
  country_whitelist: "", country_blacklist: "",
  video_file_name: "", thumbnail_name: "", poster_tall: "", subtitle_language: "",
  notes: "", link_to_pitch_deck: false,
};

const CONTENT_TYPES = ["Feature Film", "Short Film", "Episode", "Series", "Trailer", "Music Video", "Documentary"];

export default function Deliverables() {
  /* ---------- Deliverables (existing) ---------- */
  const [rows, setRows] = useState([]);
  const [openDeliv, setOpenDeliv] = useState(false);
  const [f, setF] = useState(BLANK_DELIV);

  /* ---------- Metadata Documents (new) ---------- */
  const [metaRows, setMetaRows] = useState([]);
  const [openMeta, setOpenMeta] = useState(false);
  const [m, setM] = useState(BLANK_META);
  const [showMetaSection, setShowMetaSection] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingShareId, setDownloadingShareId] = useState(null);
  const [importingImdb, setImportingImdb] = useState(false);
  const [savingPoster, setSavingPoster] = useState(false);

  const load = () => api.get("/admin/deliverables").then((r) => setRows(r.data));
  const loadMeta = () => api.get("/admin/metadata-docs").then((r) => setMetaRows(r.data));
  useEffect(() => { load(); loadMeta(); }, []);

  /* ---- Deliverables handlers ---- */
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...f,
        artwork_urls: f.artwork_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
        download_urls: f.download_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/admin/deliverables", body);
      toast.success("Deliverable created");
      setOpenDeliv(false); setF(BLANK_DELIV); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/deliverables/${id}`); load(); };
  const copyLink = (slug) => { navigator.clipboard.writeText(`${window.location.origin}/deliverables/${slug}`); toast.success("URL copied"); };

  /* ---- Metadata handlers ---- */
  const openNewMeta = () => { setM(BLANK_META); setOpenMeta(true); };
  const openEditMeta = (row) => { setM({ ...BLANK_META, ...row }); setOpenMeta(true); };

  const saveMeta = async (e) => {
    e.preventDefault();
    if (!m.title.trim()) { toast.error("Title is required"); return; }
    setSavingMeta(true);
    try {
      const payload = { ...m }; delete payload.id;
      if (m.id) {
        await api.put(`/admin/metadata-docs/${m.id}`, payload);
        toast.success("Metadata updated");
      } else {
        await api.post("/admin/metadata-docs", payload);
        toast.success("Metadata document created");
      }
      setOpenMeta(false); setM(BLANK_META); loadMeta();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally { setSavingMeta(false); }
  };

  const delMeta = async (id) => {
    if (!window.confirm("Delete this metadata document?")) return;
    try { await api.delete(`/admin/metadata-docs/${id}`); toast.success("Deleted"); loadMeta(); }
    catch { toast.error("Delete failed"); }
  };

  const downloadMeta = async (row) => {
    setDownloadingId(row.id);
    try {
      const r = await api.get(`/admin/metadata-docs/${row.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.title || "metadata").replace(/[^a-z0-9_-]+/gi, "_")}_Metadata.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Download failed: " + (e.response?.data?.detail || e.message));
    } finally { setDownloadingId(null); }
  };

  const downloadShareImage = async (row) => {
    setDownloadingShareId(row.id);
    try {
      const r = await api.get(`/admin/metadata-docs/${row.id}/share-image`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "image/jpeg" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.title || "share").replace(/[^a-z0-9_-]+/gi, "_")}_Share.jpg`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success("Share image downloaded (1200×630)");
    } catch (e) {
      toast.error("Generate share image failed: " + (e.response?.data?.detail || e.message));
    } finally { setDownloadingShareId(null); }
  };

  /* ---- IMDB import ---- */
  const importFromImdb = async () => {
    const raw = (m.imdb_id || "").trim();
    if (!raw) {
      toast.error("Enter an IMDB ID first (e.g. tt38453355) — find it in the IMDB URL");
      return;
    }
    setImportingImdb(true);
    try {
      const r = await api.get(`/admin/metadata-docs/import-imdb/${encodeURIComponent(raw)}`);
      const fetched = r.data || {};
      // Merge: only overwrite empty fields, EXCEPT for description / rating / cast / dates
      // which the user explicitly chose to import (overwrites are expected here).
      // Fields the import never touches: producers, cue_points, keywords, country_*, file names, notes.
      const updated = { ...m };
      const importedKeys = [
        "title", "content_type", "imdb_id", "description", "release_date",
        "parental_rating", "languages", "countries", "genres",
        "directors", "writers", "actors", "poster_tall",
      ];
      let filledCount = 0;
      for (const k of importedKeys) {
        if (fetched[k]) {
          updated[k] = fetched[k];
          filledCount += 1;
        }
      }
      setM(updated);
      toast.success(`Imported ${filledCount} fields from IMDB: ${fetched.title || raw}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("IMDB import failed: " + detail);
    } finally {
      setImportingImdb(false);
    }
  };

  /* ---- Save poster to local CDN ---- */
  const savePosterLocally = async () => {
    const remote = (m.poster_tall || "").trim();
    if (!remote.startsWith("http://") && !remote.startsWith("https://")) {
      toast.error("Poster is already a local file");
      return;
    }
    setSavingPoster(true);
    try {
      const r = await api.post("/admin/metadata-docs/save-poster", { url: remote });
      setM((prev) => ({ ...prev, poster_tall: r.data.url }));
      toast.success("Poster saved locally — now safe from Amazon hot-link blocks");
    } catch (e) {
      toast.error("Save poster failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingPoster(false);
    }
  };

  return (
    <div data-testid="admin-deliverables">
      {/* ---------- Header ---------- */}
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div>
          <div className="label-mono mb-2">/ Catalog</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Project Deliverables</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={openNewMeta} className="glass-btn" data-testid="meta-new">
            <FileText size={14}/> New Metadata Doc
          </button>
          <button onClick={() => setOpenDeliv(true)} className="glass-btn solid" data-testid="deliv-new">
            <Plus size={14}/> New Deliverable
          </button>
        </div>
      </div>

      {/* ---------- Metadata Documents section ---------- */}
      <section className="mb-10 rounded-2xl border border-white/5 bg-[#121215] p-5" data-testid="meta-section">
        <button
          onClick={() => setShowMetaSection((v) => !v)}
          className="w-full flex items-center justify-between mb-3"
        >
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-cyan-400"/>
            <div className="text-left">
              <div className="label-mono text-cyan-400">/ Metadata Documents</div>
              <div className="text-zinc-500 text-xs mt-1">
                {metaRows.length} {metaRows.length === 1 ? "doc" : "docs"} · Industry-standard metadata · branded PDF export
              </div>
            </div>
          </div>
          {showMetaSection ? <ChevronUp size={18} className="text-zinc-500"/> : <ChevronDown size={18} className="text-zinc-500"/>}
        </button>

        {showMetaSection && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {metaRows.map((row) => (
              <div key={row.id} className="p-4 rounded-xl border border-white/5 bg-black/30" data-testid={`meta-row-${row.id}`}>
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold truncate" title={row.title}>{row.title}</div>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">{row.content_type}</div>
                  </div>
                  <span className="badge muted text-[10px]">{row.imdb_id || "—"}</span>
                </div>
                {row.link_to_pitch_deck && (
                  <div className="text-[10px] text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 inline-flex items-center gap-1 px-2 py-0.5 rounded mb-2">
                    <Sparkles size={9}/> In Pitch Deck
                  </div>
                )}
                {row.description && (
                  <p className="text-zinc-400 text-xs mb-3 line-clamp-2">{row.description}</p>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => downloadMeta(row)}
                    disabled={downloadingId === row.id}
                    className="glass-btn ghost text-xs py-1.5 px-2.5"
                    data-testid={`meta-download-${row.id}`}
                  >
                    <Download size={12}/> {downloadingId === row.id ? "..." : "PDF"}
                  </button>
                  <button
                    onClick={() => downloadShareImage(row)}
                    disabled={downloadingShareId === row.id}
                    className="glass-btn ghost text-xs py-1.5 px-2.5"
                    title="Generate a 1200×630 branded social-share card with the poster and title — for SMS / Twitter / IG / Pulse Portal"
                    data-testid={`meta-share-${row.id}`}
                  >
                    <ImageIcon size={12}/> {downloadingShareId === row.id ? "..." : "Share"}
                  </button>
                  <button onClick={() => openEditMeta(row)} className="glass-btn ghost text-xs py-1.5 px-2.5" data-testid={`meta-edit-${row.id}`}>
                    <Edit2 size={12}/>
                  </button>
                  <button onClick={() => delMeta(row.id)} className="glass-btn ghost text-xs py-1.5 px-2.5 text-red-400" data-testid={`meta-del-${row.id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            ))}
            {metaRows.length === 0 && (
              <div className="text-zinc-500 text-sm col-span-full text-center py-8">
                No metadata documents yet. Click <strong className="text-cyan-400">New Metadata Doc</strong> to add one.
              </div>
            )}
          </div>
        )}
      </section>

      {/* ---------- Deliverables grid (existing) ---------- */}
      <div className="mb-4 label-mono text-zinc-400">/ Passkey-protected deliverables</div>
      <div className="grid md:grid-cols-2 gap-5">
        {rows.map((d) => (
          <div key={d.id} className="p-5 rounded-2xl border border-white/5 bg-[#121215]" data-testid={`deliv-${d.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-lg font-semibold">{d.title}</div>
                <div className="text-zinc-500 text-xs font-mono">/{d.slug}</div>
              </div>
              <span className="badge muted">passkey: {d.passkey}</span>
            </div>
            <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{d.synopsis}</p>
            <div className="flex gap-2">
              <button onClick={() => copyLink(d.slug)} className="glass-btn ghost p-2"><Copy size={12}/></button>
              <a href={`/deliverables/${d.slug}`} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2"><ExternalLink size={12}/></a>
              <button onClick={() => del(d.id)} className="glass-btn ghost p-2"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-zinc-500 text-sm col-span-2 text-center py-8">No deliverables yet.</div>}
      </div>

      {/* ---------- Deliverable modal ---------- */}
      <Modal open={openDeliv} onClose={() => setOpenDeliv(false)} wide title="New Deliverable" testid="deliv-modal">
        <form onSubmit={save} className="space-y-3" data-testid="deliv-form">
          <Input label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="dl-title"/>
          <div><label className="label-mono block mb-2">Synopsis *</label><textarea required className="pulse-input pulse-textarea" value={f.synopsis} onChange={(e) => setF({ ...f, synopsis: e.target.value })}/></div>
          <Input label="Trailer YouTube ID" value={f.trailer_youtube_id} onChange={(v) => setF({ ...f, trailer_youtube_id: v })}/>
          <div><label className="label-mono block mb-2">Artwork URLs (one per line)</label><textarea className="pulse-input pulse-textarea" value={f.artwork_urls} onChange={(e) => setF({ ...f, artwork_urls: e.target.value })}/></div>
          <Input label="Pitch Deck URL (optional)" value={f.pitch_deck_url} onChange={(v) => setF({ ...f, pitch_deck_url: v })}/>
          <div><label className="label-mono block mb-2">Download URLs (one per line)</label><textarea className="pulse-input pulse-textarea" value={f.download_urls} onChange={(e) => setF({ ...f, download_urls: e.target.value })}/></div>
          <div><label className="label-mono block mb-2">Metadata (free text)</label><textarea className="pulse-input pulse-textarea" value={f.metadata} onChange={(e) => setF({ ...f, metadata: e.target.value })}/></div>
          <Input label="Passkey *" required value={f.passkey} onChange={(v) => setF({ ...f, passkey: v })} testid="dl-passkey"/>
          <button className="glass-btn solid w-full justify-center" data-testid="dl-save">Create</button>
        </form>
      </Modal>

      {/* ---------- Metadata modal ---------- */}
      <Modal open={openMeta} onClose={() => setOpenMeta(false)} wide title={m.id ? "Edit Metadata Document" : "New Metadata Document"} testid="meta-modal">
        <form onSubmit={saveMeta} className="space-y-5" data-testid="meta-form">
          {/* Required basics */}
          <MetaGroup title="Project Basics">
            <MetaField label="Title *" required value={m.title} onChange={(v) => setM({ ...m, title: v })} testid="meta-title"/>
            <MetaSelect label="Content Type" value={m.content_type} onChange={(v) => setM({ ...m, content_type: v })} options={CONTENT_TYPES}/>
            <div>
              <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">IMDB ID</label>
              <div className="flex gap-2">
                <input
                  type="text" value={m.imdb_id || ""}
                  onChange={(e) => setM({ ...m, imdb_id: e.target.value })}
                  placeholder="tt38453355"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={importFromImdb}
                  disabled={importingImdb || !m.imdb_id}
                  className="glass-btn text-xs py-2 px-3 whitespace-nowrap"
                  title="Auto-fill title, plot, cast, dates, rating, and genres from IMDB"
                  data-testid="meta-import-imdb"
                >
                  <Sparkles size={12}/> {importingImdb ? "Importing…" : "Import"}
                </button>
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">
                Auto-fills title, description, dates, rating, cast, crew, and genres
              </div>
            </div>
            <MetaField label="Parental Rating" placeholder="PG-13" value={m.parental_rating} onChange={(v) => setM({ ...m, parental_rating: v })}/>
          </MetaGroup>

          <MetaGroup title="Series Info (leave blank for films)">
            <MetaField label="Series Title" value={m.series_title} onChange={(v) => setM({ ...m, series_title: v })}/>
            <MetaField label="Episode Title" value={m.episode_title} onChange={(v) => setM({ ...m, episode_title: v })}/>
            <MetaField label="Season Number" value={m.season_number} onChange={(v) => setM({ ...m, season_number: v })}/>
            <MetaField label="Episode Number" value={m.episode_number} onChange={(v) => setM({ ...m, episode_number: v })}/>
            <MetaField label="Provider Episode ID" value={m.provider_episode_id} onChange={(v) => setM({ ...m, provider_episode_id: v })}/>
            <MetaField label="Provider Series ID" value={m.provider_series_id} onChange={(v) => setM({ ...m, provider_series_id: v })}/>
          </MetaGroup>

          <MetaGroup title="Description" cols={1}>
            <MetaTextarea label="Synopsis" value={m.description} onChange={(v) => setM({ ...m, description: v })} rows={4}/>
          </MetaGroup>

          <MetaGroup title="Release & Availability">
            <MetaField label="Release Date" placeholder="1/1/2026" value={m.release_date} onChange={(v) => setM({ ...m, release_date: v })}/>
            <MetaField label="Available Date" placeholder="1/1/2026" value={m.available_date} onChange={(v) => setM({ ...m, available_date: v })}/>
            <MetaField label="Expiration Date" value={m.expiration_date} onChange={(v) => setM({ ...m, expiration_date: v })}/>
            <MetaField label="Languages" placeholder="English" value={m.languages} onChange={(v) => setM({ ...m, languages: v })}/>
            <MetaField label="Countries" placeholder="US" value={m.countries} onChange={(v) => setM({ ...m, countries: v })}/>
            <MetaField label="Genres" placeholder="Horror/Thriller" value={m.genres} onChange={(v) => setM({ ...m, genres: v })}/>
          </MetaGroup>

          <MetaGroup title="Cast & Crew">
            <MetaField label="Directors" placeholder="Nicholas Carr (KeeZY)" value={m.directors} onChange={(v) => setM({ ...m, directors: v })}/>
            <MetaField label="Writers" value={m.writers} onChange={(v) => setM({ ...m, writers: v })}/>
            <MetaField label="Producers" value={m.producers} onChange={(v) => setM({ ...m, producers: v })}/>
            <MetaField label="Actors" placeholder="comma-separated" value={m.actors} onChange={(v) => setM({ ...m, actors: v })}/>
          </MetaGroup>

          <MetaGroup title="Discovery & Territory">
            <MetaField label="Keywords" placeholder="comma-separated" value={m.keywords} onChange={(v) => setM({ ...m, keywords: v })}/>
            <MetaField label="Cue Points" value={m.cue_points} onChange={(v) => setM({ ...m, cue_points: v })}/>
            <MetaField label="Country Whitelist" value={m.country_whitelist} onChange={(v) => setM({ ...m, country_whitelist: v })}/>
            <MetaField label="Country Blacklist" value={m.country_blacklist} onChange={(v) => setM({ ...m, country_blacklist: v })}/>
          </MetaGroup>

          <MetaGroup title="Files">
            <MetaField label="Video File Name" placeholder="Road Kill (4K Master)" value={m.video_file_name} onChange={(v) => setM({ ...m, video_file_name: v })}/>
            <MetaField label="Thumbnail Name" value={m.thumbnail_name} onChange={(v) => setM({ ...m, thumbnail_name: v })}/>
            <div className="md:col-span-2">
              <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Poster (Tall)</label>
              <div className="flex gap-2 items-stretch">
                {m.poster_tall && (
                  <div className="w-12 h-16 rounded bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                    {/* preview — render any URL whether remote or local */}
                    <img
                      src={m.poster_tall.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.poster_tall}` : m.poster_tall}
                      alt="poster"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                )}
                <input
                  type="text" value={m.poster_tall || ""}
                  onChange={(e) => setM({ ...m, poster_tall: e.target.value })}
                  placeholder="https://… or /api/uploads/metadata/posters/…"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={savePosterLocally}
                  disabled={savingPoster || !m.poster_tall || m.poster_tall.startsWith("/api/")}
                  className="glass-btn text-xs py-2 px-3 whitespace-nowrap"
                  title="Download the poster from its remote URL and store it on Pulse Media's own CDN — avoids Amazon hot-link blocks on the pitch deck"
                  data-testid="meta-save-poster"
                >
                  <Save size={12}/> {savingPoster ? "Saving…" : (m.poster_tall && m.poster_tall.startsWith("/api/") ? "Local ✓" : "Save locally")}
                </button>
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">
                Imported posters point at Amazon — click <strong>Save locally</strong> to host it on your own CDN
              </div>
            </div>
            <MetaField label="Subtitle/CC Language" placeholder="English" value={m.subtitle_language} onChange={(v) => setM({ ...m, subtitle_language: v })}/>
          </MetaGroup>

          <MetaGroup title="Pitch Deck Integration" cols={1}>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/30 hover:border-cyan-400/40 cursor-pointer transition-colors" data-testid="meta-link-to-deck-row">
              <input
                type="checkbox"
                checked={!!m.link_to_pitch_deck}
                onChange={(e) => setM({ ...m, link_to_pitch_deck: e.target.checked })}
                className="mt-1 accent-cyan-400"
                data-testid="meta-link-to-deck"
              />
              <div>
                <div className="text-sm text-white font-semibold">Show this project in the Pitch Deck</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Auto-creates / updates a Pulse Portal show entry mirroring this metadata's <strong className="text-cyan-400">title, synopsis, and poster</strong>. You can still customize the show's <em>tagline</em>, <em>extended description</em>, and <em>watch link</em> from the Pitch Deck admin without losing edits.
                </div>
                {m.link_to_pitch_deck && !m.poster_tall && (
                  <div className="text-xs text-amber-400 mt-2">⚠️ No poster set — the deck show will look blank. Set Poster (Tall) above first.</div>
                )}
              </div>
            </label>
          </MetaGroup>

          <MetaGroup title="Internal Notes (optional)" cols={1}>
            <MetaTextarea label="Notes" placeholder="Internal-only notes for the team. Appears on the PDF in the notes section." value={m.notes} onChange={(v) => setM({ ...m, notes: v })} rows={3}/>
          </MetaGroup>

          <div className="flex justify-between items-center gap-2 pt-2 sticky bottom-0 bg-[#08080A] pb-1">
            <div className="text-xs text-zinc-500">All fields except Title are optional</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpenMeta(false)} className="glass-btn ghost text-sm">Cancel</button>
              <button type="submit" disabled={savingMeta} className="glass-btn solid text-sm" data-testid="meta-save">
                <FileText size={14}/> {savingMeta ? "Saving…" : (m.id ? "Update" : "Create")}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ---------- Sub-components ---------- */
function Input({ label, value, onChange, required = false, type = "text", testid }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input className="pulse-input" type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </div>
  );
}

function MetaGroup({ title, children, cols = 2 }) {
  return (
    <div>
      <div className="label-mono text-cyan-400 mb-3 text-xs uppercase tracking-wider">{title}</div>
      <div className={`grid gap-3 ${cols === 1 ? "" : "md:grid-cols-2"}`}>
        {children}
      </div>
    </div>
  );
}

function MetaField({ label, value, onChange, placeholder = "", required = false, testid }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">{label}</label>
      <input
        type="text" required={required} value={value || ""}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function MetaTextarea({ label, value, onChange, rows = 3, placeholder = "" }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">{label}</label>
      <textarea
        value={value || ""} rows={rows} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none resize-y"
      />
    </div>
  );
}

function MetaSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">{label}</label>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
